import { useMemo } from 'react';
import { Box, Typography } from '@shared/ui';
import { useSelector } from 'react-redux';

const isDateField = id => id.includes('Timestamp') || id.includes('Date') || id === 'dos';

/**
 * Filter chips strip shown above the Metrics rows. Reflects the active
 * filter set carried into the pane via `currentPageContext.filter`.
 * Returns null when no filter values are populated (matches legacy
 * `hasFilters` gate in `_legacy/InfoPane/Pages/Metrics.jsx`).
 */
const FilterStrip = ({ filter }) => {
  const { tables, infoPane } = useSelector(state => state.i18n);

  const chips = useMemo(
    () =>
      filter
        .filter(({ value }) => value.toString().replaceAll(' ', '').length > 0)
        .map(({ id, value }) => {
          const text = isDateField(id)
            ? String(value).replace(',', ' - ')
            : Array.isArray(value)
              ? value
                  .filter(e => e.length)
                  .map(e => tables[e] ?? e)
                  .join(', ')
              : String(value);
          return { id, label: tables[id] ?? id, value: text };
        }),
    [filter, tables]
  );

  if (chips.length === 0) return null;

  return (
    <Box sx={{ padding: '14px 20px 4px', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 1.2,
          color: 'var(--qhs-text-3)',
        }}
      >
        {infoPane.filtersApplied}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {chips.map(({ id, label, value }) => (
          <Box
            key={id}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              padding: '4px 10px',
              background: 'var(--qhs-cat-slate-bg, #eceef2)',
              color: 'var(--qhs-cat-slate-tx, #475569)',
              fontSize: 11.5,
              borderRadius: 6,
            }}
          >
            <b>{label}:</b>
            <span>{value}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FilterStrip;
